import React, { useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Box, Icon } from '@alto-avios/alto-ui'

import { useLiftMediaQuery } from '@reward-platform/lift/hooks/useLiftMediaQuery'
import { deviceSizes } from '@reward-platform/lift/theme/deviceSizes'

import { usePartner } from '~/context/partner'
import { useFeatureFlag } from '~/context/featureFlag'
import { useAuthContext } from '~/context/auth'
import useIntl from '~/hooks/useIntl/useIntl'
import { getLogoUrlForTheme } from '~/utils/assets'

import { usePlatformTheme } from '~/context/theme'
import HeaderMenu from '../HeaderMenu'
import { MenuLinkOption } from '../HeaderMenu/HeaderMenu.types'
import { HeaderProps } from './Header.types'

import styles from './WhitelabelHeader.module.scss'

type MenuOption = MenuLinkOption & { isVisible: boolean }

const WhitelabelHeader = ({
  userDisplayName,
  membershipNumber,
  aviosBalance = 0,
  basketItemsCount = 0,
  isLoading,
}: HeaderProps) => {
  const partner = usePartner()
  const { themeId } = usePlatformTheme()
  const intl = useIntl()
  const { isAuthenticated } = useAuthContext()
  const isTabletOrSmaller = useLiftMediaQuery(`(max-width: ${deviceSizes.medium})`)
  const orderHistoryEnabled = useFeatureFlag('order-history')

  const isUserAuthenticated = isAuthenticated && !!membershipNumber

  const t = {
    welcomeBack: intl.formatMessage({
      id: 'header-welcome-back',
      defaultMessage: 'Welcome back',
    }),
    aviosBalance: intl.formatMessage({
      id: 'header-avios-balance',
      defaultMessage: 'Avios Balance',
    }),
    orderHistory: intl.formatMessage({
      id: 'order-history',
      defaultMessage: 'Order History',
    }),
  }

  const menuOptions: MenuOption[] = useMemo(
    () => [
      {
        icon: 'file-text',
        label: t.orderHistory,
        href: '/order-history',
        opensInNewTab: false,
        isVisible: isUserAuthenticated && Boolean(orderHistoryEnabled),
      },
    ],
    [isUserAuthenticated, orderHistoryEnabled, t.orderHistory]
  )

  const getLogoSrc = (): string => {
    const shouldUseMobileLogo = themeId === 'british_airways' && isTabletOrSmaller
    return getLogoUrlForTheme(themeId, shouldUseMobileLogo)
  }

  const redirectHref = useFeatureFlag('flights') ? '/?tab=flights' : '/'

  const visibleMenuItems = menuOptions.filter(({ isVisible }) => isVisible)

  return (
    <header className={styles.headerWrapper} role="banner">
      <Box className={styles.header}>
        <Link href={redirectHref} tabIndex={0} aria-label="home link">
          <div className={styles.logo}>
            <Image
              src={getLogoSrc()}
              alt={`${partner.name} Logo`}
              layout="fill"
              objectFit="contain"
              objectPosition="left"
            />
          </div>
        </Link>
        {isUserAuthenticated && (
          <>
            <div className={styles.userDetails} data-testid="user-details">
              <span>{t.welcomeBack}</span>
              <span>{userDisplayName}</span>
            </div>
            <div className={styles.aviosDetails} data-testid="avios-details">
              <span>{t.aviosBalance}</span>
              <span>
                {aviosBalance != null && Number.isInteger(aviosBalance)
                  ? intl.formatNumber(aviosBalance, {
                      maximumFractionDigits: 0,
                    })
                  : '—'}
              </span>
            </div>
          </>
        )}
        {!isLoading && (
          <>
            <Link
              href="/orders/payment-options"
              aria-label={
                basketItemsCount > 0 ? `view basket (${basketItemsCount})` : 'view basket'
              }
            >
              <div className={styles.cartWrapper} data-testid="view-basket-cart">
                {basketItemsCount > 0 ? (
                  <>
                    <Icon iconName="cart-shopping" iconPrefix="fas" iconSize="lg" />
                    <p data-testid="basket-items-count" className={styles.basketItemsCount}>
                      {basketItemsCount}
                    </p>
                  </>
                ) : (
                  <Icon iconName="cart-shopping" iconPrefix="far" iconSize="lg" />
                )}
              </div>
            </Link>
            <HeaderMenu links={visibleMenuItems} />
          </>
        )}
      </Box>
    </header>
  )
}

export default WhitelabelHeader
